import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

const MEMBER_TOKEN_KEY = 'memberToken';
const MEMBER_KEY = 'memberInfo';

function getMemberToken() { return localStorage.getItem(MEMBER_TOKEN_KEY); }
function setMemberToken(token) { localStorage.setItem(MEMBER_TOKEN_KEY, token); }
function setMemberInfo(info) { localStorage.setItem(MEMBER_KEY, JSON.stringify(info)); }

const pageStyle = { minHeight: '100vh', backgroundColor: '#1a1a2e', color: '#ffffff', padding: '2rem 1rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '10vh' };
const cardStyle = { width: '100%', maxWidth: 420, backgroundColor: '#16213e', borderRadius: 12, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #333', backgroundColor: '#0f0f1a', color: '#fff', fontSize: '0.95rem', marginBottom: 12, boxSizing: 'border-box' };
const btnPrimary = { width: '100%', padding: '12px', borderRadius: 6, border: 'none', backgroundColor: '#ff6800', color: '#fff', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' };

export default function MemberLogin() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantNum = Number(tenant) || null;

  const [tenantName, setTenantName] = useState('');
  const [view, setView] = useState('login'); // login | setup-password
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);

  const [setupFirstname, setSetupFirstname] = useState('');
  const [setupLastname, setSetupLastname] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupConfirm, setSetupConfirm] = useState('');
  const [setupError, setSetupError] = useState(null);
  const [setupBusy, setSetupBusy] = useState(false);

  // If already logged in, redirect straight to history
  useEffect(() => {
    if (getMemberToken()) {
      navigate(`/${tenant}/publiceventhistory`, { replace: true });
    }
  }, [tenant, navigate]);

  // Load tenant name
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistoryConfig`);
        if (res.data?.tenantName) setTenantName(res.data.tenantName);
      } catch { /* ignore */ }
    })();
  }, [tenantNum]);

  const onLoginSuccess = (token, member) => {
    setMemberToken(token);
    setMemberInfo(member);
    navigate(`/${tenant}/publiceventhistory`, { replace: true });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError(null);
    try {
      const res = await axios.post(`${baseUrl}/api/members/${tenantNum}/member-login`, {
        email: loginEmail,
        password: loginPassword,
      });
      if (res.data?.success) {
        onLoginSuccess(res.data.token, res.data.member);
      }
    } catch (err) {
      if (err.response?.data?.needsPasswordSetup) {
        setView('setup-password');
      } else {
        setLoginError(err.response?.data?.message || 'Login failed');
      }
    } finally {
      setLoginBusy(false);
    }
  };

  const handleSetupPassword = async (e) => {
    e.preventDefault();
    if (setupPassword !== setupConfirm) {
      setSetupError('Passwords do not match');
      return;
    }
    setSetupBusy(true);
    setSetupError(null);
    try {
      const res = await axios.post(`${baseUrl}/api/members/${tenantNum}/member-set-password`, {
        email: loginEmail,
        firstname: setupFirstname,
        lastname: setupLastname,
        password: setupPassword,
      });
      if (res.data?.success) {
        onLoginSuccess(res.data.token, res.data.member);
      }
    } catch (err) {
      setSetupError(err.response?.data?.message || 'Failed to set password');
    } finally {
      setSetupBusy(false);
    }
  };

  if (view === 'setup-password') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', marginBottom: '0.5rem' }}>
            Set Your Password
          </h2>
          <p style={{ textAlign: 'center', color: '#aaa', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
            Enter the email and name you registered with, then choose a password.
          </p>
          <form onSubmit={handleSetupPassword}>
            <input type="email" placeholder="Email address" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required style={inputStyle} />
            <input type="text" placeholder="First name" value={setupFirstname} onChange={(e) => setSetupFirstname(e.target.value)} required style={inputStyle} />
            <input type="text" placeholder="Last name" value={setupLastname} onChange={(e) => setSetupLastname(e.target.value)} required style={inputStyle} />
            <input type="password" placeholder="New password (min 8 characters)" value={setupPassword} onChange={(e) => setSetupPassword(e.target.value)} required minLength={8} style={inputStyle} />
            <input type="password" placeholder="Confirm password" value={setupConfirm} onChange={(e) => setSetupConfirm(e.target.value)} required style={inputStyle} />
            {setupError && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 8 }}>{setupError}</p>}
            <button type="submit" disabled={setupBusy} style={{ ...btnPrimary, opacity: setupBusy ? 0.7 : 1 }}>
              {setupBusy ? 'Setting password...' : 'Set Password & Sign In'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button type="button" onClick={() => setView('login')} style={{ background: 'none', border: 'none', color: '#ff6800', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}>
              Back to sign in
            </button>
          </p>
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h2 style={{ textAlign: 'center', color: '#ff6800', marginBottom: '1.5rem' }}>
          {tenantName || 'Event History'}
        </h2>
        <p style={{ textAlign: 'center', color: '#aaa', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          Sign in to view event history
        </p>
        <form onSubmit={handleLogin}>
          <input type="email" placeholder="Email address" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required style={inputStyle} />
          <input type="password" placeholder="Password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required style={inputStyle} />
          {loginError && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 8 }}>{loginError}</p>}
          <button type="submit" disabled={loginBusy} style={{ ...btnPrimary, opacity: loginBusy ? 0.7 : 1 }}>
            {loginBusy ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p style={{ textAlign: 'center', color: '#aaa', marginTop: '1rem', fontSize: '0.85rem' }}>
          First time?{' '}
          <button type="button" onClick={() => setView('setup-password')} style={{ background: 'none', border: 'none', color: '#ff6800', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}>
            Set up your password
          </button>
        </p>
        <PoweredByFooter />
      </div>
    </div>
  );
}

function PoweredByFooter() {
  return (
    <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #333', color: '#666', fontSize: '0.85rem' }}>
      Powered by{' '}
      <a href="https://on-screen.app" target="_blank" rel="noopener noreferrer" style={{ color: '#ff6800', textDecoration: 'none' }}>
        On-Screen
      </a>
    </div>
  );
}
